'use client';

import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from '@/i18n/navigation';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import styles from './MenuPage.module.css';

/* ─── Types ─────────────────────────────────────────────── */

type MenuCategory = { id: string; label: string };
type MenuDish = {
  key: string;
  name: string;
  description: string;
  dosage?: string;
  hidePrice?: boolean;
  price: number;
  badge?: string;
};
type MenuSection = { id: string; title: string; subtitle: string; items: MenuDish[] };

interface PageData {
  hero: { eyebrow: string; title: string; description: string };
  categoryNav: { ariaLabel: string; items: MenuCategory[] };
  highlight: {
    badge: string;
    title: string;
    description: string;
    details: string;
    price: string;
    hasPrice: boolean;
    takeaway: string;
    image: { src: string; alt: string };
  };
  sections: MenuSection[];
  takeaway: {
    isVisible: boolean;
    eyebrow: string;
    title: string;
    description: string;
    primaryAction: string;
    secondaryAction: string;
  };
  taxNotice: string;
  locale: string;
}

/* ─── Easing ─────────────────────────────────────────────── */

const EASE = [0.25, 0.46, 0.45, 0.94] as const;

function TakeawayIcon() {
  return (
    <svg
      className={styles.takeawayIcon}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7.25 3.5h9.5l1.1 3.75H6.15L7.25 3.5Z" />
      <path d="M5.75 7.25h12.5l-1.05 12.5H6.8L5.75 7.25Z" />
      <path d="M9 10.25h6" />
      <path d="M9.25 13h5.5" />
      <path d="M10 15.75h4" />
    </svg>
  );
}

/* ─── Hero ───────────────────────────────────────────────── */

function MenuHero({ hero }: { hero: PageData['hero'] }) {
  const words = hero.title.split(' ');

  return (
    <section className={styles.hero}>
      <div className={styles.heroContent}>
        <motion.span
          className={styles.eyebrow}
          initial={{ y: 10 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {hero.eyebrow}
        </motion.span>

        <h1 className={styles.heroTitle} aria-label={hero.title}>
          {words.map((word, i) => (
            <motion.span
              key={i}
              className={styles.heroWord}
              initial={{ y: 36 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.65, delay: 0.08 + i * 0.055, ease: EASE }}
            >
              {word}
              {i < words.length - 1 ? '\u00A0' : ''}
            </motion.span>
          ))}
        </h1>

        <motion.p
          className={styles.heroText}
          initial={{ y: 18 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.55, ease: 'easeOut' }}
        >
          {hero.description}
        </motion.p>

        <motion.div
          className={styles.heroDivider}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
        />
      </div>
    </section>
  );
}

/* ─── Category bar ───────────────────────────────────────── */

function CategoryBar({ items, ariaLabel }: { items: MenuCategory[]; ariaLabel: string }) {
  const activeId = useScrollSpy(
    items.map((c) => c.id),
    180,
  );

  function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  }

  return (
    <div className={styles.categoryBar}>
      <nav className={styles.categoryNav} aria-label={ariaLabel}>
        {items.map((category) => {
          const isActive = activeId === category.id;
          return (
            <a
              key={category.id}
              className={`${styles.categoryLink} ${isActive ? styles.categoryLinkActive : ''}`}
              href={`#${category.id}`}
              onClick={(e) => scrollToSection(e, category.id)}
            >
              {category.label}
              {isActive && (
                <motion.span
                  className={styles.categoryIndicator}
                  layoutId="cat-indicator"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

/* ─── Highlight (plat du jour) ───────────────────────────── */

function HighlightSection({ highlight }: { highlight: PageData['highlight'] }) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      ref={ref}
      className={styles.highlightSection}
      aria-labelledby="daily-special-title"
    >
      <div className={styles.highlightGrid}>
        <motion.div
          className={styles.highlightImageFrame}
          initial={{ scale: 1.06 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ duration: 1.0, ease: EASE }}
        >
          <img
            className={styles.highlightImage}
            src={highlight.image.src}
            alt={highlight.image.alt}
          />
        </motion.div>

        <motion.article
          className={styles.highlightCard}
          initial={{ x: 40 }}
          animate={isInView ? { x: 0 } : {}}
          transition={{ duration: 0.75, delay: 0.18, ease: EASE }}
        >
          <span className={styles.highlightBadge}>{highlight.badge}</span>
          <h2 id="daily-special-title" className={styles.highlightTitle}>
            {highlight.title}
          </h2>
          <p className={styles.highlightDescription}>{highlight.description}</p>
          {highlight.details && <p className={styles.highlightDetails}>{highlight.details}</p>}
          <div className={styles.highlightFooter}>
            <span className={styles.highlightPrice}>
              {highlight.price}
              {highlight.hasPrice && <sup className={styles.priceAsterisk}>*</sup>}
            </span>
            <span className={styles.takeawayStatus}>
              <TakeawayIcon />
              {highlight.takeaway}
            </span>
          </div>
        </motion.article>
      </div>
    </section>
  );
}

/* ─── Menu item ──────────────────────────────────────────── */

function AnimatedMenuItem({
  item,
  locale,
  index,
}: {
  item: MenuDish;
  locale: string;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });

  const price = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(item.price);

  return (
    <motion.div
      ref={ref}
      className={styles.menuItem}
      initial={{ opacity: 0, y: 22 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.09, ease: 'easeOut' }}
    >
      <div className={styles.menuItemHeader}>
        <div className={styles.menuItemNameGroup}>
          <h3>{item.name}</h3>
          {item.badge && <span>{item.badge}</span>}
        </div>
        <div className={styles.menuItemPricing}>
          {item.dosage && <span className={styles.menuItemDosage}>{item.dosage}</span>}
          {!item.hidePrice && (
            <p>
              {price}
              <sup className={styles.priceAsterisk}>*</sup>
            </p>
          )}
        </div>
      </div>
      <p className={styles.menuItemDescription}>{item.description}</p>
      <div className={styles.menuItemRule} aria-hidden="true" />
    </motion.div>
  );
}

/* ─── Menu section ───────────────────────────────────────── */

function MenuSectionBlock({
  section,
  locale,
  reverse = false,
}: {
  section: MenuSection;
  locale: string;
  reverse?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section className={styles.menuSection} id={section.id} ref={ref}>
      <div className={`${styles.menuGrid} ${reverse ? styles.menuGridReverse : ''}`}>
        <motion.div
          className={styles.menuHeading}
          initial={{ opacity: 0, x: reverse ? 32 : -32 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <h2>{section.title}</h2>
          <p>{section.subtitle}</p>
        </motion.div>

        <div className={styles.menuItems}>
          {section.items.map((item, i) => (
            <AnimatedMenuItem key={item.key} item={item} locale={locale} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TTC notice ─────────────────────────────────────────── */

function TTCNotice({ text }: { text: string }) {
  return (
    <motion.div
      className={styles.ttcNotice}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <span className={styles.ttcLine} aria-hidden="true" />
      <div className={styles.ttcCard}>
        <span className={styles.ttcAsteriskIcon} aria-hidden="true">✦</span>
        <p className={styles.ttcText}>{text}</p>
      </div>
      <span className={styles.ttcLine} aria-hidden="true" />
    </motion.div>
  );
}

/* ─── CTA ────────────────────────────────────────────────── */

function CTASection({ takeaway }: { takeaway: PageData['takeaway'] }) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  if (!takeaway.isVisible) return null;

  return (
    <section className={styles.ctaSection} aria-labelledby="takeaway-title" ref={ref}>
      <motion.div
        className={styles.ctaCard}
        initial={{ opacity: 0, y: 28 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.75, ease: EASE }}
      >
        <span className={styles.ctaEyebrow}>{takeaway.eyebrow}</span>
        <h2 id="takeaway-title" className={styles.ctaTitle}>
          {takeaway.title}
        </h2>
        <p className={styles.ctaText}>{takeaway.description}</p>
        <div className={styles.ctaActions}>
          <Link href="/commande-a-emporter" className={styles.ctaPrimary}>
            {takeaway.primaryAction}
          </Link>
          <Link href="/contact" className={styles.ctaSecondary}>
            {takeaway.secondaryAction}
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

/* ─── Main export ────────────────────────────────────────── */

export function MenuPageClient({ data }: { data: PageData }) {
  return (
    <div className={styles.page}>
      <MenuHero hero={data.hero} />
      <CategoryBar items={data.categoryNav.items} ariaLabel={data.categoryNav.ariaLabel} />
      <HighlightSection highlight={data.highlight} />

      {data.sections.slice(0, 1).map((section) => (
        <MenuSectionBlock key={section.id} section={section} locale={data.locale} />
      ))}

      {data.sections.slice(1).map((section, index) => (
        <MenuSectionBlock
          key={section.id}
          section={section}
          locale={data.locale}
          reverse={index % 2 === 0}
        />
      ))}

      <TTCNotice text={data.taxNotice} />
      <CTASection takeaway={data.takeaway} />
    </div>
  );
}
